import { Button, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { ContentCopy, DeleteOutline } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { deleteManualPayment } from 'src/shared/services/paymentService';
import { FAILED_STATUSES, PaymentsStatusEnum } from 'src/shared/models/enums/paymentsStatus.enum';
import { InvoicePayment } from '../models/invoicePayment';

type PaymentDetailProps = {
  setOpenModal: (openModal: boolean) => void;
  payment: InvoicePayment;
  // När denna callback finns (t.ex. från fakturavyn) visas en radera-knapp för manuella, obokförda betalningar.
  onDeleted?: () => void;
  isMemberFeeInvoice?: boolean;
};

// Källor som registrera-betalning-formuläret sätter (manuellt registrerade). Endast dessa – och obokförda – får raderas.
const MANUAL_PAYMENT_SOURCES = ['Bankgiro', 'Plusgiro', 'Kassa'];

export const PaymentDetail = (props: PaymentDetailProps) => {
  const { setOpenModal, payment, onDeleted, isMemberFeeInvoice = false } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [tooltipText, setTooltipText] = useState<string>(String(t('common:button.copy')));
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  // Radera-knappen syns bara när en refetch-callback finns OCH betalningen är manuellt registrerad och obokförd.
  // Backend gör samma guard server-side (litar inte på frontend).
  const canDelete = !!onDeleted && !!payment.source && MANUAL_PAYMENT_SOURCES.includes(payment.source) && !payment.verificationNo;

  const deleteConfirmText = isMemberFeeInvoice
    ? `${t('invoice:payment.deleteConfirmText')} ${t('invoice:payment.deleteMemberWarning')}`
    : String(t('invoice:payment.deleteConfirmText'));

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(payment.ocr || '');
      if (isMounted()) setTooltipText(t('common:button.copied'));
    } catch (err) {
      if (isMounted()) setTooltipText(t('common:button.copyFailed'));
    }
  };

  const handleConfirmClose = async (result?: { id: string }) => {
    setConfirmOpen(false);
    if (!result) return;
    try {
      await deleteManualPayment(payment.invoicePayment_id);
      toast.success(<>{t('invoice:payment.deleteSuccess')}</>);
      setOpenModal(false);
      onDeleted?.();
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(<>{t('invoice:payment.deleteError')}</>);
    }
  };

  return (
    <>
      <GenericDialog
        open
        dialogTitle={t('invoice:payments.payment')}
        dialogContent={
          <GridWrapper narrow>
            <Grid item xs={12} sm={4}>
              <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                {t('invoice:payments.tableHeader.date')}:
              </Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
                {DateHelper.getDate(payment.date)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                {t('invoice:payments.tableHeader.amount')}:
              </Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
                <AmountWithCurrency amount={payment.amount} />
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                {t('invoice:payments.tableHeader.invoice')}:
              </Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
                {payment.ocr || '—'}{' '}
                {payment.ocr && (
                  <Tooltip title={tooltipText} onClose={() => setTooltipText(t('common:button.copy'))}>
                    <IconButton onClick={handleCopyClick}>
                      <ContentCopy fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                {t('invoice:payments.tableHeader.source')}:
              </Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
                {payment.source}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                {t('invoice:payments.verificationNo')}:
              </Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
                {payment.verificationNo || '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                {t('invoice:payments.tableHeader.status')}:
              </Typography>
              {FAILED_STATUSES.includes(payment.status as PaymentsStatusEnum) ? (
                <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1, color: 'warning.main' }}>
                  {t(`invoice:payments.status${PaymentsStatusEnum[payment.status as PaymentsStatusEnum]}`)}
                </Typography>
              ) : (
                <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
                  {t('invoice:payments.statusCompleted')}
                </Typography>
              )}
            </Grid>

            {FAILED_STATUSES.includes(payment.status as PaymentsStatusEnum) && payment.statusMessage && (
              <Grid item xs={12}>
                <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                  {t('invoice:payments.reason')}:
                </Typography>
                <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
                  {payment.statusMessage}
                </Typography>
              </Grid>
            )}

            <Grid item mb={1} xs={12} sm={4}>
              <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                {t('invoice:payments.comment')}:
              </Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
                {payment.comment}
              </Typography>
            </Grid>

            {canDelete && (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button variant='outlined' color='error' startIcon={<DeleteOutline />} onClick={() => setConfirmOpen(true)}>
                  {t('invoice:payment.deletePayment')}
                </Button>
              </Grid>
            )}
          </GridWrapper>
        }
        onClose={() => setOpenModal(false)}
        onSave={() => setOpenModal(false)}
        maxWidth='md'
        showDialogActions={false}
      />
      <DeleteConfirmationDialog
        open={confirmOpen}
        title={t('invoice:payment.deleteConfirmTitle')}
        modaltext={deleteConfirmText}
        value={String(payment.invoicePayment_id)}
        onClose={handleConfirmClose}
        confirmButtonText={t('common:button.delete')}
        confirmButtonColor='error'
      />
    </>
  );
};
